import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Plus, Trash2, Play, Square, CheckCircle2, Circle,
  ChevronLeft, ChevronRight, Lock,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { TabbedPage } from '@/components/TabbedPage';
import { useAuth } from '@/hooks/useAuth';
import { useModuleAccess } from '@/contexts/ModuleAccessContext';
import { useLabels } from '@/contexts/WorkspaceContext';
import {
  projectsService, Project, ProjectStatus, TimeEntry, ActiveTimer, Timesheet,
} from '@/services/projects.service';
import { contactsService } from '@/services/contacts.service';
import { settingsService, TeamUser } from '@/services/settings.service';
import { todayYmd } from '@/utils/date';

const STATUS_OPTIONS: Array<{ value: ProjectStatus; label: string }> = [
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const fmtHM = (minutes: number) => `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
const fmtElapsed = (s: number) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${h > 0 ? `${h}:` : ''}${String(m).padStart(h > 0 ? 2 : 1, '0')}:${String(sec).padStart(2, '0')}`;
};

export default function ProjectDetail() {
  const { projectUid } = useParams<{ projectUid: string }>();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';
  const { has } = useModuleAccess();
  const timeEnabled = has('time_tracking');
  const contactsEnabled = has('contacts');
  const t = useLabels();

  const [project, setProject] = useState<Project | null>(null);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState<TeamUser[]>([]);
  const [contactName, setContactName] = useState<string | null>(null);
  const [timer, setTimer] = useState<{ running: boolean; timer: ActiveTimer | null }>({ running: false, timer: null });
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (!projectUid) return;
    setLoading(true);
    try {
      const [p, e] = await Promise.all([
        projectsService.get(projectUid),
        timeEnabled ? projectsService.listTimeEntries({ projectUid, limit: 200 }) : Promise.resolve({ items: [] }),
      ]);
      setProject(p);
      setEntries(e.items);
      if (contactsEnabled && p.clientContactUid) {
        contactsService.get(p.clientContactUid).then((c) => setContactName([c.firstName, c.lastName].filter(Boolean).join(' '))).catch(() => {});
      } else setContactName(null);
    } catch { /* interceptor toasts; 404 leaves project null */ } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [projectUid, timeEnabled, contactsEnabled]);

  useEffect(() => {
    settingsService.listTeam().then(setTeam).catch(() => {});
  }, []);

  useEffect(() => {
    if (timeEnabled) projectsService.currentTimer().then(setTimer).catch(() => {});
    else setTimer({ running: false, timer: null });
  }, [timeEnabled]);

  useEffect(() => {
    if (!timer.running || !timer.timer) return;
    setElapsed(timer.timer.elapsedSeconds);
    const id = setInterval(() => setElapsed((v) => v + 1), 1000);
    return () => clearInterval(id);
  }, [timer]);

  const teamName = (uid: string) => {
    const u = team.find((x) => x.userUid === uid);
    return u ? [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email : uid === user?.userUid ? 'You' : 'Unknown';
  };

  const changeStatus = async (status: ProjectStatus) => {
    if (!projectUid) return;
    try {
      const updated = await projectsService.update(projectUid, { status });
      setProject((p) => (p ? { ...p, ...updated } : updated));
      toast.success('Status updated');
    } catch { /* interceptor toasts */ }
  };

  const startTimer = async () => {
    if (!projectUid) return;
    setBusy(true);
    try {
      const started = await projectsService.startTimer({ projectUid });
      setTimer({ running: true, timer: { ...started, elapsedSeconds: 0 } });
    } catch { /* interceptor toasts */ } finally { setBusy(false); }
  };

  const stopTimer = async () => {
    setBusy(true);
    try {
      await projectsService.stopTimer();
      toast.success('Timer stopped — time entry logged');
      setTimer({ running: false, timer: null });
      refresh();
    } catch { /* interceptor toasts */ } finally { setBusy(false); }
  };

  if (loading && !project) {
    return (
      <>
        <Topbar title="Loading…" breadcrumb={`${t('operations')} / ${t('projects')}`} />
        <div className="w-full px-4 py-6 sm:px-7 sm:py-7 space-y-4">
          <div className="skeleton h-24 rounded-2xl" />
          <div className="skeleton h-64 rounded-2xl" />
        </div>
      </>
    );
  }

  if (!project) {
    return (
      <>
        <Topbar title={t('projects', 'singular')} breadcrumb={`${t('operations')} / ${t('projects')}`} />
        <div className="w-full px-4 py-10 text-center text-sm text-text-muted">
          {t('projects', 'singular')} not found. <button className="text-primary underline" onClick={() => nav('/projects')}>Back to {t('projects').toLowerCase()}</button>
        </div>
      </>
    );
  }

  const dueInDays = project.dueDate ? Math.ceil((new Date(project.dueDate).getTime() - Date.now()) / 86_400_000) : null;
  const timerIsMine = timer.running && timer.timer?.projectUid === projectUid;
  const timerElsewhere = timer.running && timer.timer?.projectUid !== projectUid;

  return (
    <TabbedPage
      title={project.name}
      breadcrumb={`${t('operations')} / ${t('projects')}`}
      description={contactName ? `${t('contacts', 'singular')}: ${contactName}` : undefined}
      right={
        <div className="flex items-center gap-2">
          <Select
            value={project.status}
            onChange={(e) => changeStatus(e.target.value as ProjectStatus)}
            className="!h-9 !w-auto !text-xs"
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          {timeEnabled && (timerIsMine ? (
            <Button variant="danger" size="sm" iconLeft={<Square size={13} />} onClick={stopTimer} loading={busy}>
              {fmtElapsed(elapsed)}
            </Button>
          ) : (
            <Button variant="secondary" size="sm" iconLeft={<Play size={13} />} onClick={startTimer} loading={busy} disabled={timerElsewhere}
              title={timerElsewhere ? 'Stop your other running timer first' : undefined}>
              Start timer
            </Button>
          ))}
        </div>
      }
      tabs={[
        {
          id: 'overview',
          label: 'Overview',
          render: () => (
            <ProjectOverviewTab
              project={project}
              entries={entries}
              team={team}
              teamName={teamName}
              isAdmin={isAdmin}
              currentUserUid={user?.userUid ?? ''}
              dueInDays={dueInDays}
              onRefresh={refresh}
            />
          ),
        },
        ...timeEnabled ? [{
          id: 'my-week',
          label: 'My week',
          render: () => <MyWeekTab projectUid={project.projectUid} />,
        }] : [],
      ]}
    />
  );
}

// ─── Overview tab ───────────────────────────────────────────────────────

function ProjectOverviewTab({
  project, entries, team, teamName, isAdmin, currentUserUid, dueInDays, onRefresh,
}: {
  project: Project; entries: TimeEntry[]; team: TeamUser[]; teamName: (uid: string) => string;
  isAdmin: boolean; currentUserUid: string; dueInDays: number | null; onRefresh: () => void;
}) {
  const { has } = useModuleAccess();
  const timeEnabled = has('time_tracking');
  const summary = project.summary;
  const [milestoneName, setMilestoneName] = useState('');
  const [milestoneAdding, setMilestoneAdding] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [logDate, setLogDate] = useState(todayYmd());
  const [logHours, setLogHours] = useState('');
  const [logNote, setLogNote] = useState('');
  const [logBillable, setLogBillable] = useState(project.billingType !== 'non_billable');
  const [logUserUid, setLogUserUid] = useState(currentUserUid);
  const [busy, setBusy] = useState(false);

  const addMilestone = async () => {
    if (!milestoneName.trim()) return;
    setMilestoneAdding(true);
    try {
      await projectsService.addMilestone(project.projectUid, { name: milestoneName.trim() });
      setMilestoneName('');
      onRefresh();
    } catch { /* interceptor toasts */ } finally { setMilestoneAdding(false); }
  };

  const toggleMilestone = async (milestoneUid: string) => {
    try { await projectsService.toggleMilestone(project.projectUid, milestoneUid); onRefresh(); } catch { /* interceptor toasts */ }
  };

  const removeMilestone = async (milestoneUid: string) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm('Remove this milestone?')) return;
    try { await projectsService.removeMilestone(project.projectUid, milestoneUid); onRefresh(); } catch { /* interceptor toasts */ }
  };

  const openLogModal = (entry?: TimeEntry) => {
    if (entry) {
      setEditingEntry(entry);
      setLogDate(entry.date);
      setLogHours(String(entry.minutes / 60));
      setLogNote(entry.note ?? '');
      setLogBillable(entry.billable);
      setLogUserUid(entry.userUid);
    } else {
      setEditingEntry(null);
      setLogDate(todayYmd());
      setLogHours('');
      setLogNote('');
      setLogBillable(project.billingType !== 'non_billable');
      setLogUserUid(currentUserUid);
    }
    setLogOpen(true);
  };

  const saveEntry = async () => {
    const hours = parseFloat(logHours);
    if (!hours || hours <= 0) { toast.error('Enter hours logged'); return; }
    const minutes = Math.round(hours * 60);
    setBusy(true);
    try {
      if (editingEntry) {
        await projectsService.updateTimeEntry(editingEntry.entryUid, { date: logDate, minutes, note: logNote.trim() || undefined, billable: logBillable });
        toast.success('Time entry updated');
      } else {
        await projectsService.createTimeEntry({
          projectUid: project.projectUid, date: logDate, minutes, note: logNote.trim() || undefined, billable: logBillable,
          userUid: isAdmin ? logUserUid : undefined,
        });
        toast.success('Time logged');
      }
      setLogOpen(false);
      onRefresh();
    } catch { /* interceptor toasts */ } finally { setBusy(false); }
  };

  const removeEntry = async () => {
    if (!editingEntry) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm('Delete this time entry?')) return;
    setBusy(true);
    try {
      await projectsService.removeTimeEntry(editingEntry.entryUid);
      toast.success('Time entry deleted');
      setLogOpen(false);
      onRefresh();
    } catch { /* interceptor toasts */ } finally { setBusy(false); }
  };

  return (
    <div className="w-full px-4 py-6 sm:px-7 sm:py-7 space-y-5">
      {/* Stats tiles */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatTile label="Hours logged" value={summary ? (summary.loggedMinutes / 60).toFixed(1) : '—'} />
        <StatTile
          label="Billable amount"
          value={summary && summary.billableAmount > 0 ? `${summary.billableAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${project.currency}` : '—'}
        />
        <StatTile
          label="Budget used"
          value={summary?.budgetUsedPct != null ? `${Math.round(summary.budgetUsedPct)}%` : '—'}
          warn={summary?.budgetUsedPct != null && summary.budgetUsedPct >= 100}
        />
        <StatTile
          label="Due"
          value={dueInDays === null ? '—' : dueInDays < 0 ? `${Math.abs(dueInDays)}d overdue` : dueInDays === 0 ? 'Today' : `in ${dueInDays}d`}
          warn={dueInDays !== null && dueInDays < 0}
        />
      </div>

      <div className={clsx('grid grid-cols-1 gap-5', timeEnabled && 'lg:grid-cols-3')}>
        {/* Milestones */}
        <Card className="lg:col-span-1">
          <h3 className="mb-3 font-display text-sm font-semibold text-text-primary">Milestones</h3>
          <div className="space-y-1.5">
            {project.milestones.length === 0 && <p className="text-xs text-text-muted">No milestones yet.</p>}
            {project.milestones.map((m) => (
              <div key={m.milestoneUid} className="group flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-surface-alt">
                <button onClick={() => toggleMilestone(m.milestoneUid)} className={clsx('shrink-0', m.done ? 'text-success' : 'text-text-muted')}>
                  {m.done ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                </button>
                <span className={clsx('flex-1 text-sm', m.done && 'text-text-muted line-through')}>{m.name}</span>
                {m.dueDate && <span className="text-2xs text-text-muted">{new Date(m.dueDate).toLocaleDateString()}</span>}
                <button onClick={() => removeMilestone(m.milestoneUid)} className="shrink-0 text-text-muted opacity-0 group-hover:opacity-100 hover:text-danger">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <Input value={milestoneName} onChange={(e) => setMilestoneName(e.target.value)} placeholder="Add a milestone…"
              onKeyDown={(e) => e.key === 'Enter' && addMilestone()} />
            <Button size="sm" variant="secondary" onClick={addMilestone} loading={milestoneAdding} disabled={!milestoneName.trim()}>
              <Plus size={14} />
            </Button>
          </div>
        </Card>

        {/* Time log */}
        {timeEnabled && <Card className="lg:col-span-2 !p-0">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h3 className="font-display text-sm font-semibold text-text-primary">Time log</h3>
            <Button size="sm" iconLeft={<Plus size={13} />} onClick={() => openLogModal()}>Log time</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-2xs uppercase tracking-wide text-text-muted">
                  <th className="px-5 py-2 font-medium">Date</th>
                  <th className="px-2 py-2 font-medium">Person</th>
                  <th className="px-2 py-2 font-medium">Note</th>
                  <th className="px-2 py-2 font-medium text-right">Duration</th>
                  <th className="px-2 py-2 font-medium text-center">Billable</th>
                  <th className="px-2 py-2 font-medium text-right">Amount</th>
                  <th className="w-8 px-5 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {entries.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-8 text-center text-xs text-text-muted">No time logged yet.</td></tr>
                )}
                {entries.map((e) => {
                  const editable = !e.invoiced && (isAdmin || e.userUid === currentUserUid);
                  const amount = e.billable && e.rate ? (e.minutes / 60) * e.rate : null;
                  return (
                    <tr key={e.entryUid} className="hover:bg-surface-alt/50 cursor-pointer" onClick={() => editable && openLogModal(e)}>
                      <td className="px-5 py-2 whitespace-nowrap text-text-secondary">{e.date}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-text-secondary">{teamName(e.userUid)}</td>
                      <td className="px-2 py-2 max-w-[220px] truncate text-text-muted">{e.note ?? '—'}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-text-primary">{fmtHM(e.minutes)}</td>
                      <td className="px-2 py-2 text-center">{e.billable ? <span className="text-success">✓</span> : <span className="text-text-muted">—</span>}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-text-primary">{amount != null ? amount.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}</td>
                      <td className="px-5 py-2 text-right">{e.invoiced && <Lock size={12} className="inline text-text-muted" aria-label="Invoiced — locked" />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>}
      </div>

      <Modal open={timeEnabled && logOpen} onClose={() => !busy && setLogOpen(false)} title={editingEntry ? 'Edit time entry' : 'Log time'}>
        {isAdmin && (
          <FormField label="Logged by">
            <Select value={logUserUid} onChange={(e) => setLogUserUid(e.target.value)} disabled={!!editingEntry}>
              <option value={currentUserUid}>You</option>
              {team.filter((u) => u.userUid !== currentUserUid).map((u) => (
                <option key={u.userUid} value={u.userUid}>{[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}</option>
              ))}
            </Select>
          </FormField>
        )}
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Date" required>
            <Input type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)} />
          </FormField>
          <FormField label="Hours" required hint="e.g. 1.5 = 1h 30m">
            <Input type="number" step="0.25" min="0" value={logHours} onChange={(e) => setLogHours(e.target.value)} placeholder="1.5" />
          </FormField>
        </div>
        <FormField label="Note">
          <Textarea rows={2} value={logNote} onChange={(e) => setLogNote(e.target.value)} placeholder="What did you work on?" />
        </FormField>
        <label className="mb-3 flex items-center gap-2 text-sm text-text-secondary">
          <input type="checkbox" checked={logBillable} onChange={(e) => setLogBillable(e.target.checked)} className="h-4 w-4 rounded border-border" />
          Billable
        </label>
        <div className="flex items-center justify-between border-t border-border pt-4">
          {editingEntry ? <Button variant="ghost" onClick={removeEntry} disabled={busy}><Trash2 size={14} /> Delete</Button> : <span />}
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setLogOpen(false)} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={saveEntry} loading={busy}>{editingEntry ? 'Save' : 'Log time'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function StatTile({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-4 shadow-soft">
      <p className="text-2xs font-medium uppercase tracking-wide text-text-muted">{label}</p>
      <p className={clsx('mt-1 font-display text-xl font-bold tabular-nums', warn ? 'text-danger' : 'text-text-primary')}>{value}</p>
    </div>
  );
}

// ─── My week tab ────────────────────────────────────────────────────────

function MyWeekTab({ projectUid }: { projectUid: string }) {
  const [date, setDate] = useState(todayYmd());
  const [sheet, setSheet] = useState<Timesheet | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    projectsService.timesheet({ date }).then(setSheet).catch(() => setSheet(null)).finally(() => setLoading(false));
  }, [date]);

  const shiftWeek = (delta: number) => {
    const d = new Date(`${date}T00:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() + delta * 7);
    setDate(d.toISOString().slice(0, 10));
  };

  const dayLabels = useMemo(() => sheet?.days.map((d) => new Date(`${d}T00:00:00.000Z`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', timeZone: 'UTC' })) ?? [], [sheet]);

  return (
    <div className="w-full px-4 py-6 sm:px-7 sm:py-7 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => shiftWeek(-1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-surface-alt"><ChevronLeft size={14} /></button>
          <span className="text-sm font-medium text-text-primary">{sheet ? `${sheet.weekStart} → ${sheet.weekEnd}` : '—'}</span>
          <button onClick={() => shiftWeek(1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-surface-alt"><ChevronRight size={14} /></button>
        </div>
        {sheet && <span className="text-sm font-semibold text-text-primary">Total: {fmtHM(sheet.weekTotal)}</span>}
      </div>

      {loading && <div className="skeleton h-48 rounded-2xl" />}

      {!loading && sheet && (
        <Card className="!p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-2xs uppercase tracking-wide text-text-muted">
                <th className="px-4 py-2.5 font-medium">Project</th>
                {dayLabels.map((d, i) => <th key={i} className="px-2 py-2.5 text-right font-medium">{d}</th>)}
                <th className="px-4 py-2.5 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sheet.rows.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-xs text-text-muted">No time logged this week.</td></tr>
              )}
              {sheet.rows.map((r) => (
                <tr key={r.projectUid} className={clsx(r.projectUid === projectUid && 'bg-primary-soft/30')}>
                  <td className="px-4 py-2 flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: r.color }} />
                    {r.projectName}
                  </td>
                  {r.byDay.map((m, i) => <td key={i} className="px-2 py-2 text-right tabular-nums text-text-secondary">{m > 0 ? (m / 60).toFixed(1) : '—'}</td>)}
                  <td className="px-4 py-2 text-right font-semibold tabular-nums text-text-primary">{fmtHM(r.total)}</td>
                </tr>
              ))}
            </tbody>
            {sheet.rows.length > 0 && (
              <tfoot>
                <tr className="border-t border-border font-semibold">
                  <td className="px-4 py-2.5 text-text-primary">Total</td>
                  {sheet.dailyTotals.map((m, i) => <td key={i} className="px-2 py-2.5 text-right tabular-nums text-text-primary">{m > 0 ? (m / 60).toFixed(1) : '—'}</td>)}
                  <td className="px-4 py-2.5 text-right tabular-nums text-text-primary">{fmtHM(sheet.weekTotal)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </Card>
      )}
    </div>
  );
}
